import { useTranslation } from "@sagot/i18n";
import { Button, Icon, Label, Select, Skeleton, Switch, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useCalendarChoices, useCalendarMutations, type CalendarState } from "../use-google-calendar";

const P = "settings.integrations.google";

interface CalendarPickerProps {
  state: CalendarState;
  canManage: boolean;
}

/** Which calendar bookings go to (+ the default Google Meet link). Re-keyed by the card after a save. */
export function CalendarPicker({ state, canManage }: CalendarPickerProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const choices = useCalendarChoices(canManage);
  const { select } = useCalendarMutations();
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [meet, setMeet] = useState(state.createMeet);
  const options = choices.data ?? [];
  const current = pickedId ?? state.calendarId ?? options.find((option) => option.selected)?.id ?? options.find((option) => option.primary)?.id ?? "";
  const known = options.some((option) => option.id === state.calendarId);
  const dirty = current !== (state.calendarId ?? "") || meet !== state.createMeet;
  const save = () => {
    select.mutate(
      { calendar_id: current, create_meet: meet },
      {
        onSuccess: () => { toast.success(t(`${P}.calendarSaved`)); },
        onError: (error) => { toast.error(message(error)); },
      },
    );
  };

  if (!canManage) {
    return <p className="mt-4 text-sm"><span className="font-semibold">{t(`${P}.calendar`)}:</span> {state.calendarName ?? "—"}</p>;
  }
  if (choices.isPending) {
    return (
      <div className="mt-4 space-y-2" aria-busy="true">
        <span className="sr-only">{t(`${P}.calendarLoading`)}</span>
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }
  if (choices.isError) {
    return (
      <div className="mt-4 flex flex-wrap items-center gap-3 rounded-md bg-danger-soft p-3 text-sm" role="alert">
        <Icon name="alert-triangle" className="size-4 text-danger" />
        <span className="flex-1">{t(`${P}.calendarError`)}</span>
        <Button variant="secondary" size="sm" onClick={() => void choices.refetch()}>
          <Icon name="refresh-cw" />
          {t("common.retry")}
        </Button>
      </div>
    );
  }
  if (options.length === 0) return <p className="mt-4 text-sm text-fg-muted">{t(`${P}.calendarEmpty`)}</p>;
  return (
    <div className="mt-4 space-y-4 border-t border-line pt-4">
      <div>
        <Label htmlFor="gcal-calendar">{t(`${P}.calendar`)}</Label>
        <Select id="gcal-calendar" aria-describedby="gcal-calendar-hint" value={current} onChange={(event) => { setPickedId(event.target.value); }}>
          {known || !state.calendarId ? null : <option value={state.calendarId}>{state.calendarName ?? state.calendarId}</option>}
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.primary ? t(`${P}.primary`, { name: option.name }) : option.name}
            </option>
          ))}
        </Select>
        <p id="gcal-calendar-hint" className="hint">{t(`${P}.calendarHint`)}</p>
      </div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <label htmlFor="gcal-meet" className="text-sm font-semibold">{t(`${P}.meet`)}</label>
          <p id="gcal-meet-hint" className="mt-0.5 text-xs text-fg-subtle">{t(`${P}.meetHint`)}</p>
        </div>
        <Switch id="gcal-meet" aria-describedby="gcal-meet-hint" checked={meet} onCheckedChange={setMeet} className="mt-0.5" />
      </div>
      <div className="flex justify-end">
        <Button disabled={!dirty || current === ""} loading={select.isPending} onClick={save}>
          <Icon name="check" />
          {t(`${P}.saveCalendar`)}
        </Button>
      </div>
    </div>
  );
}
